import React, {
  createContext,
  useContext,
  ReactNode,
  useEffect,
  useRef,
} from 'react';
import { useSelector } from 'react-redux';
import { RootState, useAppDispatch } from '../store/store';
import { logout, validate } from '../store/slices/auth';
import { validate as validateApi } from '../api/services/authentication';
import { isSessionExpired } from '../api/sessionExpiry';
import { users, UserType } from '@prisma/client';
import { fetchOffers } from '../store/slices/offers';
import { fetchMessages } from '../store/slices/messages';
import { fetchShowings } from '../store/slices/showings';
import { AxiosError } from 'axios';
import usersService from '../api/services/users';
import {
  scopeHowItWorksToUser,
  syncHowItWorksSeen,
} from '../pages/HowItWorks/howItWorksStorage';
import {
  createSerialQueue,
  reconcilePushSubscription,
} from '../pwa/pushSubscription';
import {
  identifyAnalyticsUser,
  recordSignedOutSession,
} from '../utils/analytics';

interface AuthContextType {
  isAuthenticated: boolean;
  logout: () => void;
  currentUser: users | null;
  isPBuyer: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const currentUser = useSelector((state: RootState) => state.auth.user);
  const dispatch = useAppDispatch();
  const isAuthenticated = !!currentUser;

  const isPBuyer = currentUser?.type === UserType.PBUYER;

  // A fast account switch on a shared browser (sign out, sign back in as someone else)
  // can otherwise let an earlier account's slower reconcile POST land after a later
  // account's faster one, reassigning the endpoint back to the wrong person. The queue
  // guarantees the last-enqueued account is always the last one reconciled.
  const pushReconcileQueueRef = useRef(
    createSerialQueue(reconcilePushSubscription)
  );

  const identifiedUserIdRef = useRef<string | null>(null);

  const logoutDispatch = (message?: string) => {
    dispatch(logout(message));
  };

  const validateApiOnly = async () => {
    try {
      await validateApi();
      await Promise.all([
        dispatch(fetchMessages()),
        dispatch(fetchOffers()),
        dispatch(fetchShowings()),
      ]);
    } catch (error) {
      console.error(error);
      if (
        error instanceof AxiosError &&
        error.response?.data?.message === 'jwt expired'
      ) {
        logoutDispatch('Your session has expired. Please login again.');
      }
    }
  };

  const validateToken = async () => {
    try {
      const result = await dispatch(validate());
      // PostHog persists identity across visits, so a session that lapsed while the
      // tab was closed would otherwise keep attributing this browser to that person.
      // A browser with no stored token fulfills with no user rather than rejecting.
      if (validate.rejected.match(result) || !result.payload) {
        recordSignedOutSession();
      }
    } catch (error) {
      if (
        error instanceof AxiosError &&
        error.response?.data?.message === 'jwt expired'
      ) {
        logoutDispatch('Your session has expired. Please login again.');
      }
    }
  };

  const updateTimeZone = async () => {
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (currentUser?.id) {
      await usersService.patch({ timezone: timeZone }, currentUser.id);
    }
  };

  useEffect(() => {
    if (currentUser) {
      scopeHowItWorksToUser(currentUser.id);
      syncHowItWorksSeen(currentUser);
      identifyAnalyticsUser(currentUser);
      identifiedUserIdRef.current = currentUser.id;
      if (!currentUser.timezone) {
        updateTimeZone();
      }
      // A browser's push subscription outlives the session and is shared by every
      // account that signs in on this profile. Rebind it here rather than on the
      // settings page, because the person this protects is the one who never opens
      // settings — and a PBUYER, who may not own one at all, has it torn down.
      pushReconcileQueueRef.current.enqueue(currentUser);
    } else if (identifiedUserIdRef.current) {
      // Sign-out is dispatched from more than one place, so the reset keys off the
      // user disappearing rather than off any single logout handler.
      recordSignedOutSession();
      identifiedUserIdRef.current = null;
    }

    // Add event listener for visibility change
    const handleVisibilityChange = () => {
      // While a session-expired prompt is holding unsaved work, a re-validation
      // would sign the person out and discard it; the prompt owns recovery.
      if (!document.hidden && currentUser && !isSessionExpired()) {
        validateApiOnly();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    // Clean up the event listener on component unmount
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [currentUser]);

  useEffect(() => {
    if (!currentUser) {
      validateToken();
    }
  }, []);

  return (
    <AuthContext.Provider
      value={{ isAuthenticated, logout: logoutDispatch, currentUser, isPBuyer }}
    >
      {children}
    </AuthContext.Provider>
  );
};

/**
 * The auth context when a provider is present, or undefined when it is not.
 *
 * Shared components rendered outside the app shell — PropertyMap on the
 * marketing site — have no AuthProvider, and throwing there kills the route.
 */
export const useAuthOptional = () => useContext(AuthContext);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
